import { FormSwitch, isServer, Modal } from '@bob/library'
import { Button, BUTTON_WIDTH } from '@websrc/App/Components/Button/Button'
import { checkCookieConsent, getCookieConsent } from '@websrc/App/Helpers/cookieUtil'
import {
  CurrentSelection,
  SetCookieSettings,
  setCookieSettings,
} from '@websrc/App/Helpers/setCookieSettings'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { mapCookiePolicy } from '@websrc/DataMappers/mapCookiePolicy'
import { styles } from 'leds'
import React, { useEffect, useRef, useState } from 'react'

import { CoralLogo } from '../CoralLogo/CoralLogo'
import Heading from '../Heading/Heading'
import { Markdown } from '../Markdown/Markdown'
import { cookieConsent } from './CookieConsent.css'

type CookieSettingsProps = {
  acceptSettingsCallback: () => void
  cancelSettingsCallback: () => void
  current: CurrentSelection[]
}

export const CookieSettings = ({
  acceptSettingsCallback,
  cancelSettingsCallback,
  current,
}: CookieSettingsProps) => {
  const [currentSelection, setCurrentSelection] = useState(current)
  const initialSelection = useRef(currentSelection)
  const t = useStaticText()

  const acceptSelected = () =>
    setCookieSettings({
      type: 'selection',
      current: currentSelection,
      setCallback: () => {
        acceptSettingsCallback()

        // Some scripts are added when we have consent from the user.
        // When the consent is revoked, we reload to make sure those scripts are removed.
        const needsReload = currentSelection.some(
          (currSelection, i) =>
            initialSelection.current[i].selected === true && currSelection.selected === false
        )

        if (needsReload) {
          window.location.reload()
        }
      },
    })
  const [isBottom, setIsBottom] = useState(false)
  const [gradientHeight, setGradientHeight] = useState(64)
  useEffect(() => {
    const checkScroll = () => {
      const target = document.querySelector(`.${cookieConsent.top}`) as HTMLDivElement
      if (target) {
        const isReachBottom =
          target.scrollHeight - target.scrollTop <= target.clientHeight + gradientHeight
        setIsBottom(isReachBottom)
        setGradientHeight(isReachBottom ? 0 : 64)
      }
    }

    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [gradientHeight])

  return (
    <Modal
      className={cookieConsent.modal}
      closeCallback={cancelSettingsCallback}
      customContent={true}
      disableClose={true}
      overlayBackgroundColor={'dark'}
      position={'center'}
      style={{ padding: '0px', margin: '0px' }}
    >
      <div className={cookieConsent._}>
        <div
          className={cookieConsent.top}
          onScroll={(e) => {
            const target = e.target as HTMLDivElement
            const isReachBottom =
              target.scrollHeight - 1 - target.scrollTop <= target.clientHeight + gradientHeight
            if (isReachBottom) {
              setIsBottom(true)
              setGradientHeight(0)
            }
            if (!isReachBottom) {
              setIsBottom(false)
              setGradientHeight(64)
            }
          }}
        >
          <div className={cookieConsent.topLogo}>
            <CoralLogo />
          </div>
          <Heading headline={t.cookies.cookie_settings} level={1} size={'m'} />
          <div>
            {currentSelection.map((item, key) => (
              <div className={cookieConsent.settings} key={key}>
                <FormSwitch
                  {...(item.category === 'necessary' ? { disabled: true } : {})}
                  checked={item.selected}
                  label={
                    <div className={cookieConsent.settingsLabel} key={key}>
                      {item.title}
                    </div>
                  }
                  onChange={() => {
                    setCurrentSelection((currents) => [
                      ...currents.map((curr, iCurr) => ({
                        ...curr,
                        selected: iCurr === key ? !curr.selected : curr.selected,
                      })),
                    ])
                  }}
                  selectedColor={'corporateBlue'}
                />
                <Markdown
                  className={cookieConsent.descriptionSettings}
                  markdown={item.description}
                />
              </div>
            ))}
          </div>
          <div
            className={
              isBottom
                ? cookieConsent.bottom.gradientInvisible
                : cookieConsent.bottom.gradientVisible
            }
          />
        </div>
        <div className={cookieConsent.bottom._}>
          <div className={cookieConsent.bottom.buttonWrapper}>
            <Button
              color={'black'}
              colorMode={'light'}
              onClick={cancelSettingsCallback}
              size={'medium'}
              text={t.global.go_back}
              type={'secondary'}
              width={BUTTON_WIDTH.Full}
            />
            <Button
              color={'black'}
              colorMode={'light'}
              onClick={acceptSelected}
              size={'medium'}
              text={t.cookies.accept_selected}
              type={'primary'}
              width={BUTTON_WIDTH.Full}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

export interface CookieConsentProps {
  cookiePolicy: ReturnType<typeof mapCookiePolicy>
}

export function CookieConsent({ cookiePolicy }: CookieConsentProps) {
  const t = useStaticText()
  const [hasConsent, setHasConsent] = useState(checkCookieConsent())
  const [openSettings, setOpenSettings] = useState(false)

  const currentConsent = getCookieConsent()

  const cookies = cookiePolicy.preferences.map((pref) => ({
    ...pref,
    selected:
      pref.category === 'necessary' ||
      Boolean(currentConsent?.preferences?.find((cat) => cat.key === pref.category && cat.value)),
  }))

  const acceptCookies = (type: SetCookieSettings['type']) =>
    setCookieSettings({
      type,
      current: cookies,
      setCallback: () => setHasConsent(true),
    })

  const [isBottom, setIsBottom] = useState(false)
  const [gradientHeight, setGradientHeight] = useState(64)
  useEffect(() => {
    const checkScroll = () => {
      const target = document.querySelector(`.${cookieConsent.top}`) as HTMLDivElement
      if (target) {
        const isReachBottom =
          target.scrollHeight - target.scrollTop <= target.clientHeight + gradientHeight
        setIsBottom(isReachBottom)
        setGradientHeight(isReachBottom ? 0 : 64)
      }
    }

    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [gradientHeight])

  return hasConsent || isServer ? null : (
    <Modal
      className={cookieConsent.modal}
      closeCallback={() => acceptCookies('necessary')}
      customContent={true}
      disableClose={true}
      overlayBackgroundColor={'dark'}
      position={'center'}
      style={{ padding: '0px', margin: '0px' }}
    >
      <div className={cookieConsent._}>
        <div
          className={cookieConsent.top}
          onScroll={(e) => {
            const target = e.target as HTMLDivElement
            const isReachBottom =
              target.scrollHeight - 1 - target.scrollTop <= target.clientHeight + gradientHeight
            if (isReachBottom) {
              setIsBottom(true)
              setGradientHeight(0)
            }
            if (!isReachBottom) {
              setIsBottom(false)
              setGradientHeight(64)
            }
          }}
        >
          <div className={cookieConsent.topLogo}>
            <CoralLogo />
          </div>
          <Heading headline={cookiePolicy.title} level={1} size={'m'} />
          <Markdown className={cookieConsent.description} markdown={cookiePolicy.description} />
          <div
            className={
              isBottom
                ? cookieConsent.bottom.gradientInvisible
                : cookieConsent.bottom.gradientVisible
            }
          />
        </div>
        <div className={cookieConsent.bottom._}>
          <div className={styles({ backgroundColor: 'white' })}>
            <div className={cookieConsent.bottom.wrapperButtonSection}>
              <div className={cookieConsent.bottom.buttonWrapper}>
                <Button
                  color={'black'}
                  colorMode={'light'}
                  onClick={() => acceptCookies('necessary')}
                  size={'medium'}
                  text={t.cookies.just_necessary}
                  type={'secondary'}
                  width={BUTTON_WIDTH.Full}
                />
                <Button
                  color={'black'}
                  colorMode={'light'}
                  onClick={() => acceptCookies('all')}
                  size={'medium'}
                  text={t.cookies.accept_all}
                  type={'primary'}
                  width={BUTTON_WIDTH.Full}
                />
              </div>
              <div className={cookieConsent.bottom.settingsButton}>
                <Button
                  color={'blue'}
                  colorMode={'light'}
                  onClick={() => setOpenSettings(!openSettings)}
                  size={'medium'}
                  text={t.cookies.cookie_settings}
                  type={'link'}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {openSettings && (
        <CookieSettings
          acceptSettingsCallback={() => {
            setOpenSettings(false)
            setHasConsent(true)
          }}
          cancelSettingsCallback={() => setOpenSettings(false)}
          current={cookies}
        />
      )}
    </Modal>
  )
}
